/**
 * Data hook for a department view (#1229, I2 of
 * engineering/plans/project-department-dashboard.yaml).
 *
 *   useDepartmentWork(key) — fetches GET /api/departments/:key (#1227) and
 *                            returns the validated DepartmentWorkResponse:
 *                            the department's GitHub section, served from
 *                            the snapshot the new Mac's job pushes, beside
 *                            its Command Center to-dos read live.
 *
 * Refetch posture: 5 minutes. The GitHub half changes only when the
 * snapshot job pushes (every 600 s, LaunchAgent
 * com.legalflare.department-snapshot), so a faster poll would redraw the
 * same snapshot. Window-focus refetch (the global TanStack default) still
 * catches a return to the tab. Each key is its own query.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import {
  DepartmentWorkResponseSchema,
  type DepartmentWorkResponse,
} from "@command-center/shared";
import { apiGet } from "./api";

export function useDepartmentWork(
  key: string,
): UseQueryResult<DepartmentWorkResponse, Error> {
  const path = `/api/departments/${encodeURIComponent(key)}`;
  return useQuery<DepartmentWorkResponse, Error>({
    queryKey: ["departments", key],
    queryFn: async () => {
      const raw = await apiGet<unknown>(path);
      const parsed = DepartmentWorkResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `${path} failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    refetchInterval: 5 * 60_000,
  });
}
